import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Chip,
    TextField,
    InputAdornment,
    Tab,
    Tabs
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import BlogCard from '../../components/blog/BlogCard';
import Seo from '../../components/common/Seo';
import { sampleBlogPosts } from './data/samplePosts';
import { useTheme } from '@mui/material/styles';

function BlogHome() {
    const theme = useTheme();
    const [posts, setPosts] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');

    // カテゴリーの一覧（重複なく取得）
    const allCategories = ['all', ...Array.from(
        new Set(sampleBlogPosts.flatMap(post => post.categories))
    )];

    useEffect(() => {
        // 実装段階では、ここでAPIからデータを取得する
        // 現段階ではサンプルデータを使用
        setPosts(sampleBlogPosts);
        setFilteredPosts(sampleBlogPosts);
    }, []);

    useEffect(() => {
        // 検索とカテゴリーフィルター
        const filtered = posts.filter(post => {
            const matchesSearch = searchTerm === '' ||
                post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCategory = activeCategory === 'all' ||
                post.categories.includes(activeCategory);

            return matchesSearch && matchesCategory;
        });

        setFilteredPosts(filtered);
    }, [searchTerm, activeCategory, posts]);

    // 検索処理
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    // カテゴリー変更処理
    const handleCategoryChange = (event, newCategory) => {
        setActiveCategory(newCategory);
    };

    return (
        <>
            <Seo
                title="ルームシェアブログ | 生活のヒントとルームシェアの知恵"
                description="ルームシェア生活の豊かさを引き出す情報満載のブログ。初めての方から経験者まで、快適なシェア生活のためのヒントをお届けします。"
                path="/blog"
                keywords="ルームシェア,ブログ,シェアハウス,生活のヒント,節約術,ルームメイト,共同生活"
            />
            <Container maxWidth="lg">
                <Paper
                    sx={{
                        p: { xs: 2, md: 4 },
                        mt: 4,
                        mb: 6,
                        borderRadius: 2,
                        backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}30)`,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            textAlign: 'center',
                            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            color: 'transparent',
                            WebkitBackgroundClip: 'text',
                            mb: 2
                        }}
                    >
                        ルームシェアブログ
                    </Typography>
                    <Typography
                        variant="h6"
                        align="center"
                        color="text.secondary"
                        sx={{ mb: 4, maxWidth: '800px', mx: 'auto', fontWeight: 'normal' }}
                    >
                        快適なルームシェア生活のための情報、節約術、お役立ちのヒントなど、シェア生活をより豊かにする情報をお届けします。
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <TextField
                            fullWidth
                            placeholder="記事を検索..."
                            variant="outlined"
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 5,
                                    backgroundColor: '#fff',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={activeCategory}
                            onChange={handleCategoryChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            textColor="primary"
                            indicatorColor="primary"
                        >
                            {allCategories.map((category) => (
                                <Tab
                                    key={category}
                                    label={category === 'all' ? 'すべて' : category}
                                    value={category}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.95rem'
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Paper>

                {filteredPosts.length > 0 ? (
                    <Grid container spacing={3}>
                        {filteredPosts.map((post) => (
                            <Grid item xs={12} sm={6} md={4} key={post.slug}>
                                <BlogCard post={post} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                            px: 2
                        }}
                    >
                        <Typography variant="h5" gutterBottom>
                            検索条件に一致する記事が見つかりませんでした
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            別のキーワードやカテゴリーをお試しください
                        </Typography>
                    </Box>
                )}
            </Container>
        </>
    );
}

export default BlogHome; 